var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { detect } from "detect-browser";
import { AuthEventKind, AuthInfo, CoreStitchAuth, DeviceFields, StitchAuthResponseCredential, StitchClientError, StitchClientErrorCode } from "mongodb-stitch-core-sdk";
import version from "../../internal/common/Version";
import RedirectFragmentFields from "./RedirectFragmentFields";
import RedirectKeys from "./RedirectKeys";
import StitchRedirectError from "./StitchRedirectError";
import StitchUserFactoryImpl from "./StitchUserFactoryImpl";
var alphaNumericCharacters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
var StitchAuthImpl = (function (_super) {
    __extends(StitchAuthImpl, _super);
    function StitchAuthImpl(requestClient, browserAuthRoutes, authStorage, appInfo, jsdomWindow) {
        if (jsdomWindow === void 0) { jsdomWindow = window; }
        var _this = _super.call(this, requestClient, browserAuthRoutes, authStorage) || this;
        _this.browserAuthRoutes = browserAuthRoutes;
        _this.authStorage = authStorage;
        _this.appInfo = appInfo;
        _this.jsdomWindow = jsdomWindow;
        _this.listeners = new Set();
        _this.synchronousListeners = new Set();
        return _this;
    }
    Object.defineProperty(StitchAuthImpl.prototype, "userFactory", {
        get: function () {
            return new StitchUserFactoryImpl(this);
        },
        enumerable: true,
        configurable: true
    });
    StitchAuthImpl.prototype.getProviderClient = function (factory, providerName) {
        if (isAuthProviderClientFactory(factory)) {
            return factory.getClient(this, this.requestClient, this.authRoutes);
        }
        else {
            return factory.getNamedClient(providerName, this.requestClient, this.authRoutes);
        }
    };
    StitchAuthImpl.prototype.loginWithCredential = function (credential) {
        return _super.prototype.loginWithCredentialInternal.call(this, credential);
    };
    StitchAuthImpl.prototype.loginWithRedirect = function (credential) {
        var _this = this;
        var _a = this.prepareRedirect(credential), redirectUrl = _a.redirectUrl, state = _a.state;
        this.requestClient.getBaseURL().then(function (baseUrl) {
            _this.jsdomWindow.location.replace(baseUrl +
                _this.browserAuthRoutes.getAuthProviderRedirectRoute(credential, redirectUrl, state, _this.deviceInfo));
        });
    };
    StitchAuthImpl.prototype.linkWithRedirectInternal = function (user, credential) {
        var _this = this;
        if (this.user !== undefined && user.id !== this.user.id) {
            return Promise.reject(new StitchClientError(StitchClientErrorCode.UserNoLongerValid));
        }
        var _a = this.prepareRedirect(credential), redirectUrl = _a.redirectUrl, state = _a.state;
        return this.requestClient.getBaseURL().then(function (baseUrl) {
            var link = baseUrl +
                _this.browserAuthRoutes.getAuthProviderLinkRedirectRoute(credential, redirectUrl, state, _this.deviceInfo);
            return (StitchAuthImpl.injectedFetch ? StitchAuthImpl.injectedFetch : fetch)(new Request(link, {
                credentials: "include",
                headers: {
                    Authorization: "Bearer " + _this.authInfo.accessToken
                },
                mode: 'cors'
            }));
        }).then(function (response) {
            _this.jsdomWindow.location.replace(response.headers.get("X-Stitch-Location"));
        });
    };
    StitchAuthImpl.prototype.hasRedirectResult = function () {
        var isValid = false;
        try {
            isValid = this.parseRedirect().isValid;
            return isValid;
        }
        catch (_) {
            return false;
        }
        finally {
            if (!isValid) {
                this.cleanupRedirect();
            }
        }
    };
    StitchAuthImpl.prototype.handleRedirectResult = function () {
        try {
            var providerName = this.authStorage.get(RedirectKeys.ProviderName);
            var providerType = this.authStorage.get(RedirectKeys.ProviderType);
            var redirectFragment = this.parseRedirect();
            return this.loginWithCredentialInternal(new StitchAuthResponseCredential(this.processRedirectResult(redirectFragment), providerType, providerName, redirectFragment.asLink)).then(function (user) { return user; });
        }
        catch (err) {
            return Promise.reject(err);
        }
    };
    StitchAuthImpl.prototype.linkWithCredential = function (user, credential) {
        return _super.prototype.linkUserWithCredentialInternal.call(this, user, credential);
    };
    StitchAuthImpl.prototype.logout = function () {
        if (arguments.length > 0) {
            return Promise.reject(new StitchClientError(StitchClientErrorCode.UnexpectedArguments));
        }
        return _super.prototype.logoutInternal.call(this);
    };
    StitchAuthImpl.prototype.logoutUserWithId = function (userId) {
        return _super.prototype.logoutUserWithIdInternal.call(this, userId);
    };
    StitchAuthImpl.prototype.removeUser = function () {
        if (arguments.length > 0) {
            return Promise.reject(new StitchClientError(StitchClientErrorCode.UnexpectedArguments));
        }
        return _super.prototype.removeUserInternal.call(this);
    };
    StitchAuthImpl.prototype.removeUserWithId = function (userId) {
        return _super.prototype.removeUserWithIdInternal.call(this, userId);
    };
    Object.defineProperty(StitchAuthImpl.prototype, "deviceInfo", {
        get: function () {
            var info = {};
            if (this.hasDeviceId) {
                info[DeviceFields.DEVICE_ID] = this.deviceId;
            }
            if (this.appInfo.localAppName !== undefined) {
                info[DeviceFields.APP_ID] = this.appInfo.localAppName;
            }
            if (this.appInfo.localAppVersion !== undefined) {
                info[DeviceFields.APP_VERSION] = this.appInfo.localAppVersion;
            }
            var browser = detect();
            if (browser) {
                info[DeviceFields.PLATFORM] = browser.name;
                info[DeviceFields.PLATFORM_VERSION] = browser.version;
            }
            else {
                info[DeviceFields.PLATFORM] = "web";
                info[DeviceFields.PLATFORM_VERSION] = "0.0.0";
            }
            info[DeviceFields.SDK_VERSION] = version;
            return info;
        },
        enumerable: true,
        configurable: true
    });
    StitchAuthImpl.prototype.addAuthListener = function (listener) {
        this.listeners.add(listener);
        this.onAuthEvent(listener);
        this.dispatchAuthEvent({
            kind: AuthEventKind.ListenerRegistered,
        });
    };
    StitchAuthImpl.prototype.addSynchronousAuthListener = function (listener) {
        this.listeners.add(listener);
        this.onAuthEvent(listener);
        this.dispatchAuthEvent({
            kind: AuthEventKind.ListenerRegistered,
        });
    };
    StitchAuthImpl.prototype.removeAuthListener = function (listener) {
        this.listeners.delete(listener);
    };
    StitchAuthImpl.prototype.onAuthEvent = function (listener) {
        var _this = this;
        if (listener) {
            var _1 = new Promise(function (resolve) {
                if (listener.onAuthEvent) {
                    listener.onAuthEvent(_this);
                }
                resolve(undefined);
            });
        }
        else {
            this.listeners.forEach(function (one) {
                _this.onAuthEvent(one);
            });
        }
    };
    StitchAuthImpl.prototype.dispatchAuthEvent = function (event) {
        var _this = this;
        switch (event.kind) {
            case AuthEventKind.ActiveUserChanged:
                this.dispatchBlockToListeners(function (listener) {
                    if (listener.onActiveUserChanged) {
                        listener.onActiveUserChanged(_this, event.currentActiveUser, event.previousActiveUser);
                    }
                });
                break;
            case AuthEventKind.ListenerRegistered:
                this.dispatchBlockToListeners(function (listener) {
                    if (listener.onListenerRegistered) {
                        listener.onListenerRegistered(_this);
                    }
                });
                break;
            case AuthEventKind.UserAdded:
                this.dispatchBlockToListeners(function (listener) {
                    if (listener.onUserAdded) {
                        listener.onUserAdded(_this, event.addedUser);
                    }
                });
                break;
            case AuthEventKind.UserLinked:
                this.dispatchBlockToListeners(function (listener) {
                    if (listener.onUserLinked) {
                        listener.onUserLinked(_this, event.linkedUser);
                    }
                });
                break;
            case AuthEventKind.UserLoggedIn:
                this.dispatchBlockToListeners(function (listener) {
                    if (listener.onUserLoggedIn) {
                        listener.onUserLoggedIn(_this, event.loggedInUser);
                    }
                });
                break;
            case AuthEventKind.UserLoggedOut:
                this.dispatchBlockToListeners(function (listener) {
                    if (listener.onUserLoggedOut) {
                        listener.onUserLoggedOut(_this, event.loggedOutUser);
                    }
                });
                break;
            case AuthEventKind.UserRemoved:
                this.dispatchBlockToListeners(function (listener) {
                    if (listener.onUserRemoved) {
                        listener.onUserRemoved(_this, event.removedUser);
                    }
                });
                break;
            default:
                return this.assertNever(event);
        }
    };
    StitchAuthImpl.prototype.refreshCustomData = function () {
        return this.refreshAccessToken();
    };
    StitchAuthImpl.prototype.assertNever = function (x) {
        throw new Error("unexpected object: " + x);
    };
    StitchAuthImpl.prototype.dispatchBlockToListeners = function (block) {
        this.synchronousListeners.forEach(block);
        this.listeners.forEach(function (listener) {
            var _ = new Promise(function (resolve) {
                block(listener);
                resolve(undefined);
            });
        });
    };
    StitchAuthImpl.prototype.cleanupRedirect = function () {
        this.jsdomWindow.history.replaceState(null, "", this.pageRootUrl());
        this.authStorage.remove(RedirectKeys.State);
        this.authStorage.remove(RedirectKeys.ProviderName);
        this.authStorage.remove(RedirectKeys.ProviderType);
    };
    StitchAuthImpl.prototype.parseRedirect = function () {
        if (typeof this.jsdomWindow === "undefined") {
            throw new StitchRedirectError("running in a non-browser environment");
        }
        if (!this.jsdomWindow.location || !this.jsdomWindow.location.hash) {
            throw new StitchRedirectError("window location hash was undefined");
        }
        var ourState = this.authStorage.get(RedirectKeys.State);
        var redirectFragment = this.jsdomWindow.location.hash.substring(1);
        return parseRedirectFragment(redirectFragment, ourState, this.appInfo.clientAppId);
    };
    StitchAuthImpl.prototype.processRedirectResult = function (redirectFragment) {
        try {
            if (!redirectFragment.isValid) {
                throw new StitchRedirectError("invalid redirect result");
            }
            if (redirectFragment.lastError) {
                throw new StitchRedirectError("error handling redirect: " + redirectFragment.lastError);
            }
            if (!redirectFragment.authInfo) {
                throw new StitchRedirectError("no user auth value was found: it could not be decoded from fragment");
            }
        }
        catch (err) {
            throw err;
        }
        finally {
            this.cleanupRedirect();
        }
        return redirectFragment.authInfo;
    };
    StitchAuthImpl.prototype.prepareRedirect = function (credential) {
        this.authStorage.set(RedirectKeys.ProviderName, credential.providerName);
        this.authStorage.set(RedirectKeys.ProviderType, credential.providerType);
        var redirectUrl = credential.redirectUrl;
        if (redirectUrl === undefined) {
            redirectUrl = this.pageRootUrl();
        }
        var state = generateState();
        this.authStorage.set(RedirectKeys.State, state);
        return { redirectUrl: redirectUrl, state: state };
    };
    StitchAuthImpl.prototype.pageRootUrl = function () {
        return [
            this.jsdomWindow.location.protocol,
            "//",
            this.jsdomWindow.location.host,
            this.jsdomWindow.location.pathname
        ].join("");
    };
    return StitchAuthImpl;
}(CoreStitchAuth));
export default StitchAuthImpl;
function generateState() {
    var state = "";
    for (var i = 0; i < 64; ++i) {
        state += alphaNumericCharacters.charAt(Math.floor(Math.random() * alphaNumericCharacters.length));
    }
    return state;
}
function unmarshallUserAuth(data) {
    var parts = data.split("$");
    if (parts.length !== 4) {
        throw new StitchRedirectError("invalid user auth data provided while " +
            "marshalling user authentication data: " +
            data);
    }
    var _a = __read(parts, 4), accessToken = _a[0], refreshToken = _a[1], userId = _a[2], deviceId = _a[3];
    return new AuthInfo(userId, deviceId, accessToken, refreshToken);
}
var ParsedRedirectFragment = (function () {
    function ParsedRedirectFragment() {
        this.stateValid = false;
        this.clientAppIdValid = false;
        this.asLink = false;
    }
    Object.defineProperty(ParsedRedirectFragment.prototype, "isValid", {
        get: function () {
            return this.stateValid && this.clientAppIdValid;
        },
        enumerable: true,
        configurable: true
    });
    return ParsedRedirectFragment;
}());
function parseRedirectFragment(fragment, ourState, ourClientAppId) {
    var vars = fragment.split("&");
    var result = new ParsedRedirectFragment();
    vars.forEach(function (kvp) {
        var pairParts = kvp.split("=");
        var pairKey = decodeURIComponent(pairParts[0]);
        switch (pairKey) {
            case RedirectFragmentFields.StitchError:
                result.lastError = decodeURIComponent(pairParts[1]);
                break;
            case RedirectFragmentFields.UserAuth:
                try {
                    result.authInfo = unmarshallUserAuth(decodeURIComponent(pairParts[1]));
                }
                catch (e) {
                    result.lastError = e;
                }
                break;
            case RedirectFragmentFields.StitchLink:
                if (pairParts[1] === "ok") {
                    result.asLink = true;
                }
                break;
            case RedirectFragmentFields.State:
                var theirState = decodeURIComponent(pairParts[1]);
                if (ourState === theirState) {
                    result.stateValid = true;
                }
                break;
            case RedirectFragmentFields.ClientAppId:
                var clientAppId = decodeURIComponent(pairParts[1]);
                if (ourClientAppId === clientAppId) {
                    result.clientAppIdValid = true;
                }
                break;
            default:
                break;
        }
    });
    return result;
}
function isAuthProviderClientFactory(factory) {
    return (factory.getClient !== undefined);
}
//# sourceMappingURL=StitchAuthImpl.js.map